<template>
  <VCard
    :to="
      consultation.status === ScheduledConsultationStatusDTO.DELETED
        ? undefined
        : {
            name: routeNames.CONSULTATION_DETAILS,
            params: { consultationId: consultation.id },
            query: { tab: currentTab }
          }
    "
    class="py-4 pl-5 pr-6 flex gap-11 items-center rounded-lg"
  >
    <VCardItem class="p-0 flex-shrink flex-grow-0 min-w-0">
      <VCardTitle class="flex gap-2 mb-2 mt-0">
        <ConsultationChip
          :text="translateScheduledConsultationTypeDTO(consultation.type, t)"
          data-testid="consultation-type-chip"
        />
        <ConsultationChip
          :text="isFinishedConsultation ? t('Finished') : formatDateTimeWithMonth(consultationDate)"
          variant="secondary"
        />
        <ConsultationChip
          v-if="statusChipData"
          :icon="statusChipData.icon"
          :text="statusChipData.label"
          :variant="statusChipData.variant"
        />
      </VCardTitle>
      <VCardText class="p-0 py-2">
        <div class="flex flex-col gap-2">
          <section class="flex flex-col gap-2">
            <div class="flex flex-col sm:flex-row gap-2">
              <div v-if="consultation.requestedBy" class="w-full flex flex-col gap-2 min-h-[40px]">
                <div
                  v-if="consultation.type === ScheduledConsultationTypeDTO.CONFERENCE"
                  class="flex flex-row gap-2 sm:items-center"
                  data-testid="conference-topic-section"
                >
                  <div
                    class="text-[#00000080] text-[14px] leading-5 font-titillium w-[80px] flex-shrink-0"
                  >
                    {{ t('Title') }}
                  </div>
                  <div data-testid="conference-topic" class="text-body-2 truncate">
                    {{ consultation.conferenceTopic }}
                  </div>
                </div>
                <div class="flex flex-row gap-2 sm:items-center" data-testid="requested-by-section">
                  <div
                    class="text-[#00000080] text-[14px] leading-5 font-titillium w-[80px] flex-shrink-0"
                  >
                    {{ t('From') }}
                  </div>
                  <div data-testid="requested-by" class="text-body-2">
                    {{ formatPhysicianFullname(consultation.requestedBy.user) }}
                  </div>
                </div>

                <div
                  v-if="consultation.participants.length > 0"
                  class="flex flex-row gap-2 sm:items-center"
                  data-testid="participants-section"
                >
                  <div
                    class="text-[#00000080] text-[14px] leading-5 font-titillium w-[80px] flex-shrink-0"
                  >
                    {{ t('To') }}
                  </div>
                  <div class="min-w-0 flex-1 truncate">
                    <template v-for="p in consultation.participants" :key="p.user.userName">
                      <span data-testid="assigned-to" class="text-body-2 mr-1">
                        {{ formatPhysicianFullname(p.user) }}
                      </span>
                      <span
                        data-testid="medical-field"
                        class="text-[#00000080] text-titillium text-[14px] leading-5 mr-1"
                      >
                        {{ formatPhysicianDetails(p) }}
                      </span>
                    </template>
                  </div>
                </div>

                <div
                  v-if="consultation.cases.length > 0"
                  class="flex flex-row gap-2 sm:items-center"
                  data-testid="patients-section"
                >
                  <div
                    class="text-[#00000080] text-[14px] leading-5 font-titillium w-[80px] flex-shrink-0"
                  >
                    {{ t('Patients') }}
                  </div>
                  <div class="flex items-center gap-2">
                    <div
                      class="flex items-center gap-1"
                      v-for="c in consultation.cases"
                      :key="c.id"
                      data-testid="patient-item"
                    >
                      <div class="text-body-2">
                        {{ c.patient.firstName }} {{ c.patient.lastName }}
                      </div>
                      <span class="text-[#00000080] text-titillium text-[14px] leading-5">
                        ({{ formatPatientDTODetails(c.patient) }})
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </VCardText>
    </VCardItem>
    <VCardActions
      v-if="showActionButtons"
      class="flex flex-row gap-4 flex-shrink-0 flex-grow-0 ml-auto"
    >
      <ScheduledConsultationCardActions :consultation="consultation" />
    </VCardActions>
  </VCard>
</template>

<script setup lang="ts">
import {
  ScheduledConsultationStatusDTO,
  ScheduledConsultationTypeDTO,
  type ScheduledConsultationDTO
} from '@/generated'
import { useI18n } from 'vue-i18n'
import { routeNames } from '@/router/routeNames'
import ConsultationChip from './ConsultationChip.vue'
import { VCardActions } from 'vuetify/components'
import { useScheduledConsultationRequest } from '@/composables/useScheduledConsultationRequest'
import { useConsultationPatient } from '@/composables/useConsultationPatient'
import { useConsultationPhysician } from '@/composables/useConsultationPhysician'
import ScheduledConsultationCardActions from './ScheduledConsultationCardActions.vue'
import type { ConsultationsViewTab } from '@/util/consultationsTab'
import { translateScheduledConsultationTypeDTO } from '@/util/translations'
import { useFormatDate } from '@/composables/useFormatDate'

const { t } = useI18n()
const { formatDateTimeWithMonth } = useFormatDate()

const { consultation, showActionButtons = true } = defineProps<{
  consultation: ScheduledConsultationDTO
  showActionButtons?: boolean
  currentTab?: ConsultationsViewTab
}>()

const { consultationDate, isFinishedConsultation, statusChipData } =
  useScheduledConsultationRequest(consultation)

const { formatPatientDTODetails } = useConsultationPatient()
const { formatPhysicianFullname, formatPhysicianDetails } = useConsultationPhysician()
</script>
